/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { TripLegStatus } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface TripLegProps {
  id: string;
  status: TripLegStatus;
  sequence: number;
  estimatedDistanceInMiles: number | null;
  departingOdometerReading: number | null;
  departingNote: string | null;
  arrivingOdometerReading: number | null;
  arrivingNote: string | null;
  isEmergent: boolean | null;
  abortDate: Date | null;
  encodedPolyline: string | null;
  dateOnFinalMarked: Date | null;
  officialEteId: string | null;
  calculatedEteId: string | null;
  endingWaypointId: string | null;
  startingWaypointId: string | null;
  tripId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class TripLegRecord extends Record<TripLegProps>({
  id: null as any,
  status: null as any,
  sequence: null as any,
  estimatedDistanceInMiles: null as any,
  departingOdometerReading: null as any,
  departingNote: null as any,
  arrivingOdometerReading: null as any,
  arrivingNote: null as any,
  isEmergent: null as any,
  abortDate: null as any,
  encodedPolyline: null as any,
  dateOnFinalMarked: null as any,
  officialEteId: null as any,
  calculatedEteId: null as any,
  endingWaypointId: null as any,
  startingWaypointId: null as any,
  tripId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    status: TripLegStatus;
    sequence: number;
    estimatedDistanceInMiles: number | null;
    departingOdometerReading: number | null;
    departingNote: string | null;
    arrivingOdometerReading: number | null;
    arrivingNote: string | null;
    isEmergent: boolean | null;
    abortDate: string | number | Date | null;
    encodedPolyline: string | null;
    dateOnFinalMarked: string | number | Date | null;
    officialEteId: string | null;
    calculatedEteId: string | null;
    endingWaypointId: string | null;
    startingWaypointId: string | null;
    tripId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): TripLegRecord {
    if (json == null) {
      throw new Error('TripLegRecord.fromJS: parameter cannot be null');
    }
    return new TripLegRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'TripLegJson.id',
      ),
      status: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'status', TripLegStatus.Planned),
        'TripLegJson.status',
      ),
      sequence: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'sequence', 0),
        'TripLegJson.sequence',
      ),
      estimatedDistanceInMiles: json.estimatedDistanceInMiles,
      departingOdometerReading: json.departingOdometerReading,
      departingNote: json.departingNote,
      arrivingOdometerReading: json.arrivingOdometerReading,
      arrivingNote: json.arrivingNote,
      isEmergent: json.isEmergent,
      abortDate: json.abortDate != null ? new Date(json.abortDate) : null,
      encodedPolyline: json.encodedPolyline,
      dateOnFinalMarked: json.dateOnFinalMarked != null ? new Date(json.dateOnFinalMarked) : null,
      officialEteId: json.officialEteId,
      calculatedEteId: json.calculatedEteId,
      endingWaypointId: json.endingWaypointId,
      startingWaypointId: json.startingWaypointId,
      tripId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'tripId', '00000000-0000-0000-0000-000000000000'),
        'TripLegJson.tripId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'TripLegJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    status: TripLegStatus;
    sequence: number;
    estimatedDistanceInMiles: number | null;
    departingOdometerReading: number | null;
    departingNote: string | null;
    arrivingOdometerReading: number | null;
    arrivingNote: string | null;
    isEmergent: boolean | null;
    abortDate: string | number | Date | null;
    encodedPolyline: string | null;
    dateOnFinalMarked: string | number | Date | null;
    officialEteId: string | null;
    calculatedEteId: string | null;
    endingWaypointId: string | null;
    startingWaypointId: string | null;
    tripId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [TripLegRecord, Error[]] {
    if (json == null) {
      throw new Error('TripLegRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new TripLegRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'TripLegJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'status', TripLegStatus.Planned),
            'TripLegJson.status',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      sequence: tryOrDefault(
        () =>
          getFromOrThrow(json, (j) => propValueOrDefault(j, 'sequence', 0), 'TripLegJson.sequence'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      estimatedDistanceInMiles: tryOrDefault(
        () => json.estimatedDistanceInMiles,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      departingOdometerReading: tryOrDefault(
        () => json.departingOdometerReading,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      departingNote: tryOrDefault(
        () => json.departingNote,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      arrivingOdometerReading: tryOrDefault(
        () => json.arrivingOdometerReading,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      arrivingNote: tryOrDefault(
        () => json.arrivingNote,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isEmergent: tryOrDefault(
        () => json.isEmergent,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      abortDate: tryOrDefault(
        () => (json.abortDate != null ? new Date(json.abortDate) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      encodedPolyline: tryOrDefault(
        () => json.encodedPolyline,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateOnFinalMarked: tryOrDefault(
        () => (json.dateOnFinalMarked != null ? new Date(json.dateOnFinalMarked) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      officialEteId: tryOrDefault(
        () => json.officialEteId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      calculatedEteId: tryOrDefault(
        () => json.calculatedEteId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      endingWaypointId: tryOrDefault(
        () => json.endingWaypointId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      startingWaypointId: tryOrDefault(
        () => json.startingWaypointId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      tripId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'tripId', '00000000-0000-0000-0000-000000000000'),
            'TripLegJson.tripId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'TripLegJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: TripLegProps) {
    super(props);
    if (props == null) {
      throw new Error('TripLegRecord constructor parameter cannot be null');
    }
  }
}

export abstract class TripLegViewModelBase implements TripLegProps {
  protected readonly _props: TripLegProps;
  private _json?: TripLegProps;

  get id(): string {
    return this._props.id;
  }
  get status(): TripLegStatus {
    return this._props.status;
  }
  get sequence(): number {
    return this._props.sequence;
  }
  get estimatedDistanceInMiles(): number | null {
    return this._props.estimatedDistanceInMiles;
  }
  get departingOdometerReading(): number | null {
    return this._props.departingOdometerReading;
  }
  get departingNote(): string | null {
    return this._props.departingNote;
  }
  get arrivingOdometerReading(): number | null {
    return this._props.arrivingOdometerReading;
  }
  get arrivingNote(): string | null {
    return this._props.arrivingNote;
  }
  get isEmergent(): boolean | null {
    return this._props.isEmergent;
  }
  get abortDate(): Date | null {
    return this._props.abortDate;
  }
  get encodedPolyline(): string | null {
    return this._props.encodedPolyline;
  }
  get dateOnFinalMarked(): Date | null {
    return this._props.dateOnFinalMarked;
  }
  get officialEteId(): string | null {
    return this._props.officialEteId;
  }
  get calculatedEteId(): string | null {
    return this._props.calculatedEteId;
  }
  get endingWaypointId(): string | null {
    return this._props.endingWaypointId;
  }
  get startingWaypointId(): string | null {
    return this._props.startingWaypointId;
  }
  get tripId(): string {
    return this._props.tripId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isCancelled() {
    return this._props.status === TripLegStatus.Cancelled;
  }
  public get isComplete() {
    return this._props.status === TripLegStatus.Complete;
  }
  public get isEnRoute() {
    return this._props.status === TripLegStatus.EnRoute;
  }
  public get isPlanned() {
    return this._props.status === TripLegStatus.Planned;
  }

  public isAnyStatus(...tripLegStatus: TripLegStatus[]) {
    return tripLegStatus.includes(this._props.status);
  }

  constructor(props: TripLegProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        status: this._props.status,
        sequence: this._props.sequence,
        estimatedDistanceInMiles: this._props.estimatedDistanceInMiles,
        departingOdometerReading: this._props.departingOdometerReading,
        departingNote: this._props.departingNote,
        arrivingOdometerReading: this._props.arrivingOdometerReading,
        arrivingNote: this._props.arrivingNote,
        isEmergent: this._props.isEmergent,
        abortDate: this._props.abortDate,
        encodedPolyline: this._props.encodedPolyline,
        dateOnFinalMarked: this._props.dateOnFinalMarked,
        officialEteId: this._props.officialEteId,
        calculatedEteId: this._props.calculatedEteId,
        endingWaypointId: this._props.endingWaypointId,
        startingWaypointId: this._props.startingWaypointId,
        tripId: this._props.tripId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllTripLegEntities = (state: AppState) => state.entityStores.tripLeg.entities;

export const selectTripLegById = (id: string) => (state: AppState) =>
  state.entityStores.tripLeg.entities.get(id);

export const selectTripLegMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.tripLeg.meta.get(id);

export const selectTripLegAndMetadataById = (id: string) =>
  createSelector(selectTripLegById(id), selectTripLegMetadataById(id), (tripLeg, meta) => ({
    tripLeg,
    meta,
  }));

export function selectTripLegEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllTripLegEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, TripLegRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectTripLegEntitiesByStatus(status: TripLegStatus) {
  return createSelector(selectAllTripLegEntities, (entities) =>
    entities.filter((e) => e.status === status).valueSeq(),
  );
}

export function selectTripLegEntitiesBySequence(sequence: number) {
  return createSelector(selectAllTripLegEntities, (entities) =>
    entities.filter((e) => e.sequence === sequence).valueSeq(),
  );
}

export function selectTripLegEntitiesByEstimatedDistanceInMiles(estimatedDistanceInMiles: number) {
  return createSelector(selectAllTripLegEntities, (entities) =>
    entities.filter((e) => e.estimatedDistanceInMiles === estimatedDistanceInMiles).valueSeq(),
  );
}

export function selectTripLegEntitiesByDepartingOdometerReading(departingOdometerReading: number) {
  return createSelector(selectAllTripLegEntities, (entities) =>
    entities.filter((e) => e.departingOdometerReading === departingOdometerReading).valueSeq(),
  );
}

export function selectTripLegEntitiesByDepartingNote(departingNote: string) {
  return createSelector(selectAllTripLegEntities, (entities) =>
    entities.filter((e) => e.departingNote === departingNote).valueSeq(),
  );
}

export function selectTripLegEntitiesByArrivingOdometerReading(arrivingOdometerReading: number) {
  return createSelector(selectAllTripLegEntities, (entities) =>
    entities.filter((e) => e.arrivingOdometerReading === arrivingOdometerReading).valueSeq(),
  );
}

export function selectTripLegEntitiesByArrivingNote(arrivingNote: string) {
  return createSelector(selectAllTripLegEntities, (entities) =>
    entities.filter((e) => e.arrivingNote === arrivingNote).valueSeq(),
  );
}

export function selectTripLegEntitiesByIsEmergent(isEmergent: boolean) {
  return createSelector(selectAllTripLegEntities, (entities) =>
    entities.filter((e) => e.isEmergent === isEmergent).valueSeq(),
  );
}

export function selectTripLegEntitiesByAbortDate(abortDate: Date) {
  return createSelector(selectAllTripLegEntities, (entities) =>
    entities.filter((e) => e.abortDate === abortDate).valueSeq(),
  );
}

export function selectTripLegEntitiesByEncodedPolyline(encodedPolyline: string) {
  return createSelector(selectAllTripLegEntities, (entities) =>
    entities.filter((e) => e.encodedPolyline === encodedPolyline).valueSeq(),
  );
}

export function selectTripLegEntitiesByDateOnFinalMarked(dateOnFinalMarked: Date) {
  return createSelector(selectAllTripLegEntities, (entities) =>
    entities.filter((e) => e.dateOnFinalMarked === dateOnFinalMarked).valueSeq(),
  );
}

export function selectTripLegEntitiesByOfficialEteId(officialEteId: string) {
  return createSelector(selectAllTripLegEntities, (entities) =>
    entities.filter((e) => e.officialEteId === officialEteId).valueSeq(),
  );
}

export function selectTripLegEntitiesByCalculatedEteId(calculatedEteId: string) {
  return createSelector(selectAllTripLegEntities, (entities) =>
    entities.filter((e) => e.calculatedEteId === calculatedEteId).valueSeq(),
  );
}

export function selectTripLegEntitiesByEndingWaypointId(endingWaypointId: string) {
  return createSelector(selectAllTripLegEntities, (entities) =>
    entities.filter((e) => e.endingWaypointId === endingWaypointId).valueSeq(),
  );
}

export function selectTripLegEntitiesByStartingWaypointId(startingWaypointId: string) {
  return createSelector(selectAllTripLegEntities, (entities) =>
    entities.filter((e) => e.startingWaypointId === startingWaypointId).valueSeq(),
  );
}

export function selectTripLegEntitiesByTripId(tripId: string) {
  return createSelector(selectAllTripLegEntities, (entities) =>
    entities.filter((e) => e.tripId === tripId).valueSeq(),
  );
}

export function selectTripLegEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllTripLegEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
